import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api } from './api';
import { DEFAULT_TEAM, Team } from './slides';

export type Entry = { v: any; by?: string; at?: string };
type Status = 'saved' | 'saving' | 'offline';

interface Ctx {
  slug: string;
  loaded: boolean;
  notFound: boolean;
  data: Record<string, Entry>;
  get: (k: string) => any;
  meta: (k: string) => Entry | undefined;
  set: (k: string, v: any) => void;
  canEdit: boolean;
  author: string;
  team: Team;
  status: Status;
  unlock: (code: string, author: string) => Promise<void>;
  lock: () => void;
}

const DeckCtx = createContext<Ctx>(null as any);
export const EditAllowed = createContext(true);
export const StaticCtx = createContext(false);
export const useDeck = () => useContext(DeckCtx);
export const useCanEdit = () => {
  const d = useDeck();
  const allowed = useContext(EditAllowed);
  return d.canEdit && allowed;
};

const K = { data: (s: string) => `deck:${s}:data`, unlocked: (s: string) => `deck:${s}:unlocked`, legacyCode: (s: string) => `deck:${s}:code`, pending: (s: string) => `deck:${s}:pending`, author: 'deck:author' };
const readJSON = (k: string, d: any) => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : d; } catch { return d; } };

export function rememberRecent(slug: string, title: string) {
  const list = readJSON('deck:recent', []).filter((r: any) => r.slug !== slug);
  localStorage.setItem('deck:recent', JSON.stringify([{ slug, title }, ...list].slice(0, 5)));
}

export function DeckProvider({ slug, children }: { slug: string; children: React.ReactNode }) {
  const [data, setData] = useState<Record<string, Entry>>(() => readJSON(K.data(slug), {}));
  const [canEdit, setCanEdit] = useState<boolean>(() => !!localStorage.getItem(K.unlocked(slug)));
  const [author, setAuthor] = useState(() => localStorage.getItem(K.author) || '');
  const [loaded, setLoaded] = useState(() => !!localStorage.getItem(K.data(slug)));
  const [notFound, setNotFound] = useState(false);
  const [status, setStatus] = useState<Status>('saved');
  const pending = useRef<Record<string, any>>(readJSON(K.pending(slug), {}));
  const timers = useRef<Record<string, any>>({});
  const authorRef = useRef(author);
  authorRef.current = author;

  useEffect(() => { localStorage.setItem(K.data(slug), JSON.stringify(data)); }, [data, slug]);
  const savePending = () => localStorage.setItem(K.pending(slug), JSON.stringify(pending.current));

  const dropCode = useCallback(() => {
    localStorage.removeItem(K.unlocked(slug));
    setCanEdit(false);
  }, [slug]);

  const refresh = useCallback(async () => {
    try {
      const legacy = localStorage.getItem(K.legacyCode(slug));
      if (legacy) {
        localStorage.removeItem(K.legacyCode(slug));
        await api.verify(slug, legacy).then(() => localStorage.setItem(K.unlocked(slug), '1')).catch(() => {});
      }
      const r = await api.get(slug);
      setNotFound(false);
      if (r.can_edit) localStorage.setItem(K.unlocked(slug), '1');
      else localStorage.removeItem(K.unlocked(slug));
      setCanEdit(r.can_edit);
      setData((prev) => {
        const next: Record<string, Entry> = { ...r.data };
        Object.keys(pending.current).forEach((k) => { next[k] = { ...(prev[k] || {}), v: pending.current[k] }; });
        return JSON.stringify(next) === JSON.stringify(prev) ? prev : next;
      });
      rememberRecent(slug, r.title || '');
      if (!Object.keys(pending.current).length) setStatus('saved');
    } catch (e: any) {
      if (e?.response?.status === 404) setNotFound(true);
      else if (Object.keys(pending.current).length) setStatus('offline');
    } finally {
      setLoaded(true);
    }
  }, [slug]);

  const flush = useCallback(async (k: string) => {
    const v = pending.current[k];
    if (v === undefined) return;
    setStatus('saving');
    try {
      const e = await api.put(slug, k, v, authorRef.current);
      if (pending.current[k] === v) {
        delete pending.current[k];
        savePending();
        setData((p) => ({ ...p, [k]: e }));
      }
      if (!Object.keys(pending.current).length) setStatus('saved');
    } catch (err: any) {
      if (err?.response?.status === 401) {
        toast.error('Code de groupe invalide : modification non enregistrée.');
        pending.current = {};
        savePending();
        dropCode();
        setStatus('saved');
      } else {
        setStatus('offline');
        timers.current[k] = setTimeout(() => flush(k), 5000);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, dropCode]);

  const set = useCallback((k: string, v: any) => {
    pending.current[k] = v;
    savePending();
    setData((p) => ({ ...p, [k]: { ...(p[k] || {}), v } }));
    setStatus('saving');
    clearTimeout(timers.current[k]);
    timers.current[k] = setTimeout(() => flush(k), 700);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flush]);

  useEffect(() => {
    refresh();
    Object.keys(pending.current).forEach((k) => flush(k));
    const id = setInterval(refresh, 4000);
    return () => clearInterval(id);
  }, [refresh, flush]);

  const unlock = async (c: string, a: string) => {
    await api.verify(slug, c);
    localStorage.setItem(K.unlocked(slug), '1');
    localStorage.setItem(K.author, a);
    setAuthor(a);
    setCanEdit(true);
    refresh();
  };

  const lock = () => { api.logout(slug).catch(() => {}); dropCode(); };

  const get = useCallback((k: string) => data[k]?.v, [data]);
  const meta = useCallback((k: string) => data[k], [data]);
  const team: Team = { ...DEFAULT_TEAM, ...(data.team?.v || {}) };

  return (
    <DeckCtx.Provider value={{ slug, loaded, notFound, data, get, meta, set, canEdit, author, team, status, unlock, lock }}>
      {children}
    </DeckCtx.Provider>
  );
}

export function useChannel(slug: string, onMsg: (m: any) => void) {
  const ref = useRef(onMsg);
  ref.current = onMsg;
  const chan = useRef<BroadcastChannel | null>(null);
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const c = new BroadcastChannel(`deck-${slug}`);
    c.onmessage = (e) => ref.current(e.data);
    chan.current = c;
    return () => c.close();
  }, [slug]);
  return useCallback((m: any) => chan.current?.postMessage(m), []);
}
